// admin-grid.ts — the admin grids' machinery adapter (seam
// users-audit-parity, R59a). ONE wiring of the release grid's column
// machinery — per-column sheets-style filters, three-state pins, the header
// context menu, Copy Column, the column picker and ui_state persistence —
// parameterised by seat key so #/users and #/audit cannot drift one verb at
// a time the way two hand-rolled copies would.
//
// This file OWNS NO POLICY: the filter/pin/copy vocabulary is
// grid-machinery.ts's (the one decoration pass), the menu rosters and preset
// reconciliation are home.ts's exported pure surfaces (headerMenuSpec,
// columnPickerEntries, reconcileColumnState), and the menu chrome is
// ctxmenu.ts's singleton. It deliberately imports FROM home.ts rather than
// duplicating: a twin copy of headerMenuSpec here is exactly the
// two-subtly-different-admin-UIs failure admin-ui.ts's header warns about.
// (The import is one-way — home.ts does not import this file — so no cycle;
// admin-ui.ts could not host this without one, since home.ts already imports
// admin-ui's avatarNode.)
//
// The seat rides the EXISTING keyed grid-preset kind (server ui-state.ts:
// kind 'grid_preset' × key = preset name — the key namespace is free-form by
// design). Keys 'users'/'audit' join 'home'; NO KINDS edit, NO migration.
//
// What the admin grids DON'T get from home: the row verb menu (Open/Fork/
// Abort/... are release verbs — a user row and an audit row have no
// lifecycle), so a data-cell right-click offers exactly Copy Value.

import type { ColDef, GridApi } from 'ag-grid-community';
import {
  anyColumnFilterActive,
  columnCopyText,
  effectivePins,
  parseGridSeatBlob,
  togglePin,
  withGridMachinery,
  type ColumnFilterMap,
} from './grid-machinery.js';
import {
  columnPickerEntries,
  headerMenuSpec,
  reconcileColumnState,
  type HeaderMenuEntry,
} from './home.js';
import { mountColumnPicker } from './grid-toolbar.js';
import { attachCtxHandler, copyPlainText, openCtxMenu, type CtxMenuItem } from './ctxmenu.js';
import { jsonFetch } from './admin-ui.js';

export interface AdminGridMachineryOpts<T> {
  /** ui_state grid-preset seat key — 'users' or 'audit' (joins 'home'). */
  presetKey: string;
  /** Shipped default pins. BOTH admin grids ship [] deliberately (R59a
   * decision, stated in the seam PR): unlike the 14-column release grid,
   * these grids fit their viewport with flex columns, so a shipped pin buys
   * no keep-visible benefit and costs a permanent split border. The
   * three-state contract is unchanged — a user's pin persists in the seat;
   * absent = these defaults; [] = deliberately unpinned. */
  defaultPins: readonly string[];
  /** colIds that take NO filter (the users actions cell has no values). */
  unfiltered?: readonly string[];
  /** Canonical column order, for reconciling a saved preset with columns
   * shipped after it was saved (home.ts reconcileColumnState's defect). */
  columnIds: readonly string[];
  /** The eye's vocabulary for one cell ('' = blank) — what the filter
   * lists, Copy emits and doesFilterPass matches. */
  displayValue(colId: string, row: T): string;
  /** The rows the filter's value list counts over (the page's full
   * dataset — the quick-search box narrows the GRID, not this list). */
  rows(): T[];
  /** Page-specific row verbs for the right-click menu (bridge-ui-polish,
   * owner item E): rendered ABOVE the generic Copy Value, separated. Absent
   * = the pre-E behavior (a data cell offers exactly Copy Value). The page
   * decides labels/icons/enablement; the machinery owns only the chrome
   * and the generic tail. */
  rowMenu?(row: T): CtxMenuItem[];
}

export interface AdminGridHosts {
  gridEl: HTMLElement;
  colsBtn: HTMLButtonElement | null;
  colsMenu: HTMLElement | null;
  resetBtn: HTMLButtonElement | null;
}

export interface AdminGridMachinery<T> {
  /** The one decoration pass — call on the page's ColDefs BEFORE makeGrid,
   * so every column (including ones added later) participates. */
  decorate(defs: ColDef<T>[]): ColDef<T>[];
  /** Debounced seat write — hand to onColumnMoved/Resized/Visible. */
  persist(): void;
  /** Wire menus/picker/reset AFTER the grid exists and restore the saved
   * preset. Returns the teardown the page must call on unmount. */
  wire(api: GridApi<T>, hosts: AdminGridHosts): Promise<() => void>;
}

export function adminGridMachinery<T>(opts: AdminGridMachineryOpts<T>): AdminGridMachinery<T> {
  let api: GridApi<T> | null = null;
  let defs: ColDef<T>[] = [];
  /** null = shipped defaults; an array = the user's explicit list
   * ([] = deliberately unpinned) — the home grid's exact three-state seat. */
  let pins: string[] | null = null;
  let colFilters: ColumnFilterMap = {};
  let saveTimer: number | null = null;
  const seatUrl = `/api/ui-state/grid-preset/${opts.presetKey}`;

  // ONE blob carries columns + pins + colFilters (home's item-55.8 shape;
  // parseGridSeatBlob reads it back). Unconditional {state} PUT — the
  // server's no-rev LWW lane, same as grid-preset/home.
  function persist(): void {
    if (!api) return;
    if (saveTimer) window.clearTimeout(saveTimer);
    saveTimer = window.setTimeout(() => {
      const blob: Record<string, unknown> = { columns: api!.getColumnState() };
      if (pins !== null) blob.pins = pins; // absent = shipped defaults
      if (anyColumnFilterActive(colFilters)) blob.colFilters = colFilters;
      void jsonFetch('PUT', seatUrl, { state: blob });
    }, 750);
  }

  function decorate(input: ColDef<T>[]): ColDef<T>[] {
    defs = input;
    return withGridMachinery(input, {
      defaultPins: opts.defaultPins,
      unfiltered: opts.unfiltered ?? [],
      getFilter: (c) => colFilters[c],
      setFilter: (c, s) => {
        if (s === null) delete colFilters[c];
        else colFilters[c] = s;
      },
      displayValue: (c, r) => opts.displayValue(c, r as T),
      rows: () => opts.rows() as unknown[],
      onFilterWrite: () => persist(),
    });
  }

  async function wire(gridApi: GridApi<T>, hosts: AdminGridHosts): Promise<() => void> {
    api = gridApi;
    const teardowns: Array<() => void> = [];
    const ids = defs.map((d) => d.colId).filter((c): c is string => !!c);

    /** Pin exactly the effective set, everything else unpinned — one
     * authoritative pass (SET semantics; home.ts applyPins). */
    function applyPins(): void {
      api?.applyColumnState({
        state: effectivePins(pins, opts.defaultPins, ids)
          .map((colId) => ({ colId, pinned: 'left' as const })),
        defaultState: { pinned: null },
      });
    }

    /** Displayed rows in PAINT order — Copy Column emits what the screen
     * shows (post sort, post filter, post quick-search). */
    function displayedRows(): T[] {
      const out: T[] = [];
      const n = api?.getDisplayedRowCount() ?? 0;
      for (let i = 0; i < n; i++) {
        const d = api?.getDisplayedRowAtIndex(i)?.data;
        if (d) out.push(d);
      }
      return out;
    }

    function clearAllFilters(): void {
      colFilters = {};
      void api?.setFilterModel(null); // comps mirror via setModel(null)
      api?.refreshHeader();
      persist();
    }

    function resetColumnsToDefault(): void {
      pins = null;
      api?.resetColumnState(); // decorated defs carry the default pins
      applyPins(); // explicit second pass — measured, not assumed
      api?.refreshHeader();
      // The persisted columns/pins are CLEARED, not overwritten with a copy
      // of the defaults. Filters stay — this is a columns-only action.
      if (saveTimer) {
        window.clearTimeout(saveTimer);
        saveTimer = null;
      }
      const keep = anyColumnFilterActive(colFilters) ? { colFilters } : null;
      void jsonFetch('PUT', seatUrl, { state: keep });
    }

    // ---- the column picker: the SHARED primitive (grid-toolbar.ts) ------
    const picker = hosts.colsBtn && hosts.colsMenu
      ? mountColumnPicker({
        btn: hosts.colsBtn,
        menu: hosts.colsMenu,
        entries: () => {
          if (!api) return [];
          const state = api.getColumnState() as Array<{ colId?: string; hide?: boolean | null }>;
          return columnPickerEntries(defs as Array<{ colId?: string; headerName?: string; hide?: boolean }>, state);
        },
        onToggle: (colId, on) => { api?.setColumnsVisible([colId], on); },
      })
      : null;
    if (picker) teardowns.push(() => picker.destroy());

    /** Customize Columns from the header menu — deferred one tick past the
     * menu click's own bubble to the picker's document dismiss (home.ts
     * openColumnsPopover). */
    function openColumnsPopover(): void {
      window.setTimeout(() => { picker?.open(); }, 0);
    }

    // ---- the header context menu (home's item-55.6 roster, verbatim) ----
    function openHeaderMenu(colId: string, x: number, y: number): void {
      const eff = effectivePins(pins, opts.defaultPins, ids);
      const runs: Record<HeaderMenuEntry['id'], () => void> = {
        customize: openColumnsPopover,
        'copy-column': () =>
          void copyPlainText(columnCopyText(displayedRows().map((r) => opts.displayValue(colId, r)))),
        pin: () => {
          pins = togglePin(eff, colId);
          applyPins();
          persist();
        },
        autosize: () => {
          api?.autoSizeAllColumns();
          persist(); // AG's autosize source is not a user column event
        },
        reset: resetColumnsToDefault,
        'clear-filters': clearAllFilters,
      };
      const items: CtxMenuItem[] = [];
      for (const s of headerMenuSpec(eff.includes(colId), anyColumnFilterActive(colFilters))) {
        if (s.sep) items.push({ label: '—', icon: '', sep: true });
        items.push({
          label: s.label,
          icon: s.icon,
          disabled: !s.enabled,
          ...(s.tip ? { tip: s.tip } : {}),
          run: runs[s.id],
        });
      }
      openCtxMenu(x, y, items);
    }

    // ONE delegated listener on the grid HOST (home's rebuild-proof shape).
    const onGridCtx = (e: MouseEvent): boolean => {
      const t = e.target as HTMLElement;
      const head = t.closest<HTMLElement>('.ag-header-cell[col-id]');
      if (head) {
        const colId = head.getAttribute('col-id') ?? '';
        if (colId) {
          openHeaderMenu(colId, e.clientX, e.clientY);
          return true;
        }
        return false;
      }
      const rowEl = t.closest<HTMLElement>('[row-index]');
      if (!rowEl) return false;
      const idx = Number(rowEl.getAttribute('row-index'));
      if (!Number.isFinite(idx)) return false;
      const data = api?.getDisplayedRowAtIndex(idx)?.data;
      if (!data) return false;
      const cellCol = t.closest<HTMLElement>('.ag-cell[col-id]')?.getAttribute('col-id') ?? null;
      // Page verbs first (owner item E), the generic Copy Value last —
      // home.ts's row-menu order. Without a rowMenu seat AND without a cell
      // under the pointer there is nothing to offer; the native menu stays.
      const items: CtxMenuItem[] = opts.rowMenu ? opts.rowMenu(data) : [];
      if (cellCol) {
        if (items.length) items.push({ label: '—', icon: '', sep: true });
        items.push({
          label: 'Copy Value',
          icon: 'copy',
          run: () => void copyPlainText(opts.displayValue(cellCol, data)),
        });
      }
      // Without a rowMenu seat AND without a cell under the pointer there is
      // nothing page-specific to offer; falling through hands the event to
      // the surface fallback, which offers the copy actions the OS menu would
      // have — never the OS menu itself.
      if (!items.length) return false;
      openCtxMenu(e.clientX, e.clientY, items);
      return true;
    };
    // Through the ONE menu primitive (PR-3b): it owns the listener and the
    // editable-text ruling; the rows resolve through AG's displayed-row index
    // here, which is what the boolean declares.
    teardowns.push(attachCtxHandler(hosts.gridEl, onGridCtx));

    // ---- the toolbar Reset: the both-at-once verb (home's R28 order —
    // clearAllFilters arms the debounced write; resetColumnsToDefault
    // cancels that timer and PUTs the cleared seat: one write, no race) ----
    hosts.resetBtn?.addEventListener('click', () => {
      clearAllFilters();
      resetColumnsToDefault();
    });

    // ---- restore the persisted preset (404-tolerant; legacy-shape safe) --
    const preset = await jsonFetch<{ state?: unknown }>('GET', seatUrl);
    if (preset.status === 200 && preset.data?.state !== undefined && preset.data.state !== null) {
      const blob = parseGridSeatBlob(preset.data.state);
      if (Array.isArray(blob.columns) && blob.columns.length) {
        // Reconciled, never applied raw — a preset predating a column would
        // exile it past the right edge (home.ts's measured defect).
        const state = reconcileColumnState(blob.columns as Array<{ colId?: string }>, opts.columnIds);
        if (state.length) api!.applyColumnState({ state: state as never, applyOrder: true });
      }
      pins = blob.pins ?? null;
      if (blob.colFilters) colFilters = { ...blob.colFilters };
    }
    // Pins AFTER the columns blob — the pins field (or its absence =
    // defaults) is the one authority (home.ts's ordering note).
    applyPins();
    // Seed AG's mirrored filter models so the header funnels tell the truth
    // after a restore (setModel writes the same state back — idempotent).
    const activeFilterCols = Object.keys(colFilters);
    for (const c of activeFilterCols) {
      void api!.setColumnFilterModel(c, colFilters[c] as never).catch(() => {});
    }
    if (activeFilterCols.length) api!.onFilterChanged();

    teardowns.push(() => {
      if (saveTimer) {
        window.clearTimeout(saveTimer);
        saveTimer = null;
      }
    });
    return () => {
      for (const fn of teardowns.splice(0)) {
        try { fn(); } catch { /* one bad teardown must not strand the rest */ }
      }
    };
  }

  return { decorate, persist, wire };
}
